import React, { useEffect, useRef } from 'react';
import { Button } from '../../ui';
import { Drawer } from '../../ui/Drawer';
import { useBuilderEditor } from '../../../features/builder/BuilderEditorContext';
import GeneralTab from './tabs/GeneralTab';
import ValidationTab from './tabs/ValidationTab';
import LogicTab from './tabs/LogicTab';
import AdvancedTab from './tabs/AdvancedTab';
import HelpTab from './tabs/HelpTab';
import ActionsTab from './tabs/ActionsTab';
import RemarksTab from './tabs/RemarksTab';
import '../../layout/builder-shell.scss';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'validation', label: 'Validation' },
  { id: 'logic', label: 'Logic' },
  { id: 'advanced', label: 'Advanced' },
  { id: 'help', label: 'Help' },
  { id: 'actions', label: 'Actions' },
  { id: 'remarks', label: 'Remarks' },
];

const PropertyPanel = ({ isOpen, onClose, isMobile }) => {
  const {
    fieldeditid, isNewField, selectedanswertype, showAnswerTypePicker, tabchoosed, setTabChoosed,
    handleSaveField, handleSaveActions, resetFieldProperties, isActionNeeded, isRemarkNeeded,
    getActionDetails, guardAction,
  } = useBuilderEditor();

  const bodyRef = useRef(null);
  const hasSelection = fieldeditid || (isNewField && selectedanswertype && !showAnswerTypePicker);

  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'actions' && !isActionNeeded()) return false;
    if (t.id === 'remarks' && !isRemarkNeeded()) return false;
    return true;
  });

  const activeTab = visibleTabs.some((t) => t.id === tabchoosed) ? tabchoosed : 'general';

  useEffect(() => {
    bodyRef.current?.scrollTo(0, 0);
  }, [activeTab]);

  if (!isOpen && isMobile) return null;

  const renderTab = () => {
    switch (activeTab) {
      case 'general': return <GeneralTab />;
      case 'validation': return <ValidationTab />;
      case 'logic': return <LogicTab />;
      case 'advanced': return <AdvancedTab />;
      case 'help': return <HelpTab />;
      case 'actions': return <ActionsTab />;
      case 'remarks': return <RemarksTab />;
      default: return <GeneralTab />;
    }
  };

  const panelContent = (
    <>
      {!hasSelection ? (
        <div className="builder-property-panel__empty">
          <p>Select a question to edit its properties</p>
          <p className="builder-text-secondary">Or add a new question from the toolbar</p>
        </div>
      ) : (
        <>
          <div className="builder-property-panel__header">
            <h3 className="builder-h3">{isNewField ? 'New Question' : 'Edit Question'}</h3>
          </div>
          <div className="builder-property-panel__tabs" role="tablist">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`builder-property-panel__tab ${activeTab === tab.id ? 'builder-property-panel__tab--active' : ''}`}
                onClick={() => {
                  setTabChoosed(tab.id);
                  bodyRef.current?.scrollTo(0, 0);
                  if (tab.id === 'actions') getActionDetails();
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div ref={bodyRef} className="builder-property-panel__body" role="tabpanel">
            {renderTab()}
          </div>
          <div className="builder-property-panel__footer">
            <Button variant="secondary" onClick={() => guardAction(() => { resetFieldProperties(); onClose?.(); })}>Cancel</Button>
            {activeTab === 'actions' ? (
              <Button variant="primary" onClick={handleSaveActions}>Save actions</Button>
            ) : activeTab === 'logic' ? null : (
              <Button variant="primary" onClick={() => handleSaveField(!!fieldeditid)}>Save</Button>
            )}
          </div>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer show={isOpen} onClose={onClose} title={isNewField ? 'New Question' : 'Edit Question'} position="bottom">
        <div className="builder-property-panel" aria-label="Question properties">
          {panelContent}
        </div>
      </Drawer>
    );
  }

  return (
    <aside className="builder-property-panel" aria-label="Question properties">
      {panelContent}
    </aside>
  );
};

export default PropertyPanel;
